/**
 * @rogueworlds/shared — the frozen wire contract for protocol v1.
 *
 * This module is the ONLY coupling between the browser client, the Node/Colyseus authority behind
 * the Cloudflare Tunnel and the voxel engine. It intentionally depends on nothing: no DOM, no Node,
 * no third-party package, so the exact same code runs in a Worker, in a browser and under vitest.
 *
 * Runtime-independence rules honoured here:
 * - `toBase64` / `fromBase64` are pure integer arithmetic over char codes (no `btoa`/`atob`,
 *   no `Buffer`, no `TextEncoder`).
 * - `decodeMessage` never trusts `JSON.parse` output: the `t` discriminator is checked against the
 *   known tag set and every field of the matching variant is validated before the value is
 *   returned. Unknown `t` -> `RangeError`, malformed payload -> `TypeError`.
 *
 * v1 message list (see CONTRACTS.md):
 * - client -> server: `hello`, `input`, `setBlock`, `sub`, `ping`, `unsub`.
 * - server -> client: `welcome`, `snapshot`, `chunk`, `ack`, `blockAck`, `pong`, `error`.
 *
 * `unsub` releases interest in chunks the client no longer renders, so server-side interest can
 * shrink again instead of only growing. It stays inside protocol v1 (PROTOCOL_VERSION is not
 * bumped) because a new client message is additive. A server advertises it through the optional
 * `welcome.capabilities` list; an old server omits that field entirely.
 */

/** Wire protocol revision. Bumped only together with a CONTRACTS.md change request. */
export const PROTOCOL_VERSION = 1;

/** Voxels per chunk axis. A chunk is CHUNK_SIZE^3 voxels (32^3). */
export const CHUNK_SIZE = 32;

/** Total voxels in a chunk: CHUNK_SIZE ** 3. Frozen value, checked by a test. */
export const CHUNK_VOXELS = 32768;

/** Edge length of one voxel in meters (10 cm micro-voxels). */
export const VOXEL_SIZE_M = 0.1;

/** Authoritative simulation rate in Hz (20 Hz = a 50 ms tick, fixed per ADR-0009). */
export const DEFAULT_TICK_HZ = 20;

/**
 * Canonical chunk coordinate key: `"cx,cy,cz"` with three integers (negatives allowed).
 * String keys are stable across client and Worker, which is what makes chunk subscriptions,
 * cache eviction and `setBlock` routing hash-equivalent on both sides.
 */
export type ChunkKey = string;

const CHUNK_KEY_RE = /^-?[0-9]+,-?[0-9]+,-?[0-9]+$/;

/** Encodes integer chunk coordinates into the canonical `ChunkKey`. Throws on non-integers. */
export function encodeChunkKey(cx: number, cy: number, cz: number): ChunkKey {
  if (!Number.isInteger(cx) || !Number.isInteger(cy) || !Number.isInteger(cz)) {
    throw new TypeError(`encodeChunkKey expects integer chunk coordinates, got (${cx}, ${cy}, ${cz})`);
  }
  return `${cx},${cy},${cz}`;
}

function parseKeyPart(part: string | undefined, key: string): number {
  if (part === undefined || !/^-?[0-9]+$/.test(part)) {
    throw new RangeError(`decodeChunkKey: "${key}" is not a canonical "cx,cy,cz" key`);
  }
  const value = Number(part);
  if (!Number.isSafeInteger(value)) {
    throw new RangeError(`decodeChunkKey: component "${part}" of "${key}" is out of range`);
  }
  return value;
}

/** Decodes a canonical `ChunkKey` back into integers. Throws `RangeError` on malformed input. */
export function decodeChunkKey(key: ChunkKey): { cx: number; cy: number; cz: number } {
  if (typeof key !== "string" || !CHUNK_KEY_RE.test(key)) {
    throw new RangeError(`decodeChunkKey: "${String(key)}" is not a canonical "cx,cy,cz" key`);
  }
  const parts = key.split(",");
  return {
    cx: parseKeyPart(parts[0], key),
    cy: parseKeyPart(parts[1], key),
    cz: parseKeyPart(parts[2], key),
  };
}

/** Right-handed, Y-up vector. Units are meters for positions, unit-length for intents. */
export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** Voxel block id. 0 is air; ids are 8-bit on the wire so the palette stays small. */
export type BlockId = number;

/**
 * Serialized chunk state. `blocks` is `base64(rleEncode(Uint8Array(CHUNK_VOXELS)))`, i.e. a run
 * length encoded block-id stream in x-fastest, then z, then y order.
 */
export interface ChunkSnapshot {
  key: ChunkKey;
  cx: number;
  cy: number;
  cz: number;
  rev: number;
  palette: number[];
  blocks: string;
}

/** Replicated player state. Position is the player's feet (not the eye). */
export interface PlayerState {
  id: string;
  name: string;
  pos: Vec3;
  yaw: number;
  pitch: number;
  tick: number;
}

/** World parameters handed to the client in the `welcome` frame. */
export interface WorldConfig {
  tickHz: number;
  chunkSize: number;
  voxelSize: number;
  viewDistanceChunks: number;
  maxPlayers: number;
}

/** MVP defaults, matching the frozen contract constants. */
export const DEFAULT_WORLD_CONFIG: WorldConfig = {
  tickHz: DEFAULT_TICK_HZ,
  chunkSize: CHUNK_SIZE,
  voxelSize: VOXEL_SIZE_M,
  viewDistanceChunks: 8,
  maxPlayers: 64,
};

/**
 * The ONE cap on how far a client may see, in chunk columns: the client clamps its own view radius to
 * it, and the server derives its residency bound from it, so the two can never disagree about how many
 * chunks a legitimate client streams.
 *
 * It is here rather than in either half on purpose. The client used to keep a private cap while the
 * server derived its interest bound from `DEFAULT_WORLD_CONFIG.viewDistanceChunks`; raising the
 * config — or a hostile `welcome` naming a larger radius — then made the client request more columns
 * than the server would admit, and since a client marks a key as subscribed the moment it asks for it,
 * the refused keys were permanent holes in the rendered world. One exported number removes the
 * possibility: the client clamps to `MAX_VIEW_DISTANCE_CHUNKS`, the server clamps the config it
 * advertises to `MAX_VIEW_DISTANCE_CHUNKS`, and the server's `MAX_INTEREST_CHUNKS` is derived from the
 * same value.
 *
 * Raising it is a protocol-level capacity decision, not a tuning knob: the server's residency cap has
 * to cover the whole disc at `MAX_VIEW_DISTANCE_CHUNKS * 1.15` before this may grow (see
 * `apps/server/src/world/room.ts`, whose test asserts that inequality).
 */
export const MAX_VIEW_DISTANCE_CHUNKS = 8;

/**
 * Capability advertising the `unsub` client message: a server includes it in `welcome.capabilities`
 * when it understands interest release, so a client can keep its old `sub`-only behaviour instead of
 * sending a frame the older server has no `onMessage` handler for: Colyseus drops the connection.
 */
export const CAPABILITY_UNSUB = "unsub";

/** Client -> server frames. */
export type ClientMessage =
  | { t: "hello"; v: number; name: string }
  | { t: "input"; seq: number; dt: number; move: Vec3; look: Vec3; actions: number }
  | { t: "setBlock"; key: ChunkKey; x: number; y: number; z: number; block: BlockId; rev: number }
  | { t: "sub"; keys: ChunkKey[] }
  | { t: "ping"; ts: number }
  | { t: "unsub"; keys: ChunkKey[] };

/** Server -> client frames. */
export type ServerMessage =
  | {
      t: "welcome";
      playerId: string;
      tick: number;
      worldSeed: number;
      spawn: Vec3;
      config: WorldConfig;
      /**
       * Optional, additive capability list. Absence means "no optional capabilities" (an older
       * server omits the field entirely), so a client must degrade gracefully rather than send a
       * frame that server's `onMessage` registry has no handler for: the framework then closes the
       * connection, which this client treats as terminal. Unknown values must be ignored.
       */
      capabilities?: readonly string[];
    }
  | { t: "snapshot"; tick: number; players: PlayerState[] }
  | { t: "chunk"; snapshot: ChunkSnapshot }
  | { t: "ack"; seq: number; tick: number; pos: Vec3; vel: Vec3 }
  | { t: "blockAck"; key: ChunkKey; x: number; y: number; z: number; block: BlockId; rev: number; ok: boolean }
  | { t: "pong"; ts: number }
  | { t: "error"; code: string; message: string };

const CLIENT_TAGS: readonly string[] = ["hello", "input", "setBlock", "sub", "ping", "unsub"];
const SERVER_TAGS: readonly string[] = ["welcome", "snapshot", "chunk", "ack", "blockAck", "pong", "error"];

/** Serializes a frame to the JSON text wire format. */
export function encodeMessage(msg: ClientMessage | ServerMessage): string {
  if (!isRecord(msg)) {
    throw new TypeError("encodeMessage expects a message object");
  }
  return JSON.stringify(msg);
}

/** Parses and fully validates a frame. Throws `TypeError` on malformed frames, `RangeError` on unknown `t`. */
export function decodeMessage<T = ServerMessage>(raw: string): T {
  if (typeof raw !== "string") {
    throw new TypeError("decodeMessage expects a string frame");
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw) as unknown;
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    throw new TypeError(`decodeMessage: frame is not valid JSON (${detail})`);
  }
  if (!isRecord(parsed)) {
    throw new TypeError("decodeMessage: frame must be a JSON object");
  }
  const tag = parsed["t"];
  if (typeof tag !== "string") {
    throw new TypeError('decodeMessage: frame is missing its string "t" discriminator');
  }
  if (isClientMessage(parsed)) {
    return parsed as unknown as T;
  }
  if (isServerMessage(parsed)) {
    return parsed as unknown as T;
  }
  if (CLIENT_TAGS.includes(tag) || SERVER_TAGS.includes(tag)) {
    throw new TypeError(`decodeMessage: malformed "${tag}" frame`);
  }
  throw new RangeError(`decodeMessage: unknown message type "${tag}"`);
}

/**
 * Run-length encodes a block stream into `[value, runLength]` pairs.
 * Run lengths are one byte; runs longer than 255 are split into consecutive pairs.
 */
export function rleEncode(data: Uint8Array): Uint8Array {
  const length = data.length;
  if (length === 0) {
    return new Uint8Array(0);
  }
  // Worst case is one pair per byte (strictly alternating values).
  const out = new Uint8Array(length * 2);
  let cursor = 0;
  let value = data[0] ?? 0;
  let run = 1;
  for (let i = 1; i < length; i++) {
    const next = data[i] ?? 0;
    if (next === value && run < 255) {
      run += 1;
      continue;
    }
    out[cursor] = value;
    out[cursor + 1] = run;
    cursor += 2;
    value = next;
    run = 1;
  }
  out[cursor] = value;
  out[cursor + 1] = run;
  cursor += 2;
  return out.slice(0, cursor);
}

/**
 * Decodes `[value, runLength]` pairs. Throws `RangeError` when the byte stream is not a valid RLE
 * encoding or when the decoded length does not equal `expectedLength`.
 */
export function rleDecode(data: Uint8Array, expectedLength: number): Uint8Array {
  if (!Number.isInteger(expectedLength) || expectedLength < 0) {
    throw new RangeError(`rleDecode: expectedLength must be a non-negative integer, got ${expectedLength}`);
  }
  if (data.length % 2 !== 0) {
    throw new RangeError(`rleDecode: RLE stream length ${data.length} is not a multiple of 2`);
  }
  const out = new Uint8Array(expectedLength);
  let cursor = 0;
  for (let i = 0; i < data.length; i += 2) {
    const value = data[i] ?? 0;
    const run = data[i + 1] ?? 0;
    if (run === 0) {
      throw new RangeError(`rleDecode: zero-length run at byte ${i} (value ${value})`);
    }
    if (cursor + run > expectedLength) {
      throw new RangeError(
        `rleDecode: run at byte ${i} overflows the expected length (need ${cursor + run}, have ${expectedLength})`,
      );
    }
    out.fill(value, cursor, cursor + run);
    cursor += run;
  }
  if (cursor !== expectedLength) {
    throw new RangeError(`rleDecode: decoded ${cursor} bytes, expected ${expectedLength}`);
  }
  return out;
}

const BASE64_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

function base64Index(code: number): number {
  if (code >= 65 && code <= 90) return code - 65; // A-Z
  if (code >= 97 && code <= 122) return code - 71; // a-z
  if (code >= 48 && code <= 57) return code + 4; // 0-9
  if (code === 43) return 62; // +
  if (code === 47) return 63; // /
  return -1;
}

/** Standard padded base64 encoding using only integer arithmetic (works in Worker and browser). */
export function toBase64(bytes: Uint8Array): string {
  const parts: string[] = [];
  const length = bytes.length;
  for (let i = 0; i < length; i += 3) {
    const b0 = bytes[i] ?? 0;
    const b1 = bytes[i + 1] ?? 0;
    const b2 = bytes[i + 2] ?? 0;
    const triple = (b0 << 16) | (b1 << 8) | b2;
    parts.push(BASE64_ALPHABET.charAt((triple >>> 18) & 63));
    parts.push(BASE64_ALPHABET.charAt((triple >>> 12) & 63));
    parts.push(i + 1 < length ? BASE64_ALPHABET.charAt((triple >>> 6) & 63) : "=");
    parts.push(i + 2 < length ? BASE64_ALPHABET.charAt(triple & 63) : "=");
  }
  return parts.join("");
}

/** Strict base64 decoder. Accepts padded or unpadded input; throws `RangeError` on bad characters. */
export function fromBase64(s: string): Uint8Array {
  if (typeof s !== "string") {
    throw new TypeError("fromBase64 expects a string");
  }
  let end = s.length;
  while (end > 0 && s.charAt(end - 1) === "=") {
    end -= 1;
  }
  const padding = s.length - end;
  if (padding > 2) {
    throw new RangeError(`fromBase64: "${s}" has ${padding} padding characters`);
  }
  const dataChars = end;
  if (dataChars === 0) {
    return new Uint8Array(0);
  }
  if (dataChars % 4 === 1 || (padding > 0 && (dataChars + padding) % 4 !== 0)) {
    throw new RangeError(`fromBase64: "${s}" has an invalid base64 length`);
  }
  const out = new Uint8Array(Math.floor((dataChars * 3) / 4));
  let written = 0;
  let accumulator = 0;
  let bits = 0;
  for (let i = 0; i < dataChars; i++) {
    const value = base64Index(s.charCodeAt(i));
    if (value < 0) {
      throw new RangeError(`fromBase64: invalid base64 character "${s.charAt(i)}" at index ${i}`);
    }
    accumulator = (accumulator << 6) | value;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out[written] = (accumulator >>> bits) & 255;
      written += 1;
    }
  }
  return out;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

function isInteger(v: unknown): v is number {
  return typeof v === "number" && Number.isInteger(v);
}

function isString(v: unknown): v is string {
  return typeof v === "string";
}

function isBoolean(v: unknown): v is boolean {
  return typeof v === "boolean";
}

function isVec3(v: unknown): v is Vec3 {
  return isRecord(v) && isFiniteNumber(v["x"]) && isFiniteNumber(v["y"]) && isFiniteNumber(v["z"]);
}

function isChunkKey(v: unknown): v is ChunkKey {
  return typeof v === "string" && CHUNK_KEY_RE.test(v);
}

function isLocalVoxelCoord(v: unknown): v is number {
  return isInteger(v) && v >= 0 && v < CHUNK_SIZE;
}

function isBlockId(v: unknown): v is BlockId {
  return isInteger(v) && v >= 0 && v <= 255;
}

function isPlayerState(v: unknown): v is PlayerState {
  return (
    isRecord(v) &&
    isString(v["id"]) &&
    isString(v["name"]) &&
    isVec3(v["pos"]) &&
    isFiniteNumber(v["yaw"]) &&
    isFiniteNumber(v["pitch"]) &&
    isInteger(v["tick"])
  );
}

function isWorldConfig(v: unknown): v is WorldConfig {
  return (
    isRecord(v) &&
    isFiniteNumber(v["tickHz"]) &&
    isInteger(v["chunkSize"]) &&
    isFiniteNumber(v["voxelSize"]) &&
    isInteger(v["viewDistanceChunks"]) &&
    isInteger(v["maxPlayers"])
  );
}

function isChunkSnapshot(v: unknown): v is ChunkSnapshot {
  return (
    isRecord(v) &&
    isChunkKey(v["key"]) &&
    isInteger(v["cx"]) &&
    isInteger(v["cy"]) &&
    isInteger(v["cz"]) &&
    isInteger(v["rev"]) &&
    Array.isArray(v["palette"]) &&
    (v["palette"] as unknown[]).every(isBlockId) &&
    isString(v["blocks"])
  );
}

function isBlockEdit(v: Record<string, unknown>): boolean {
  return (
    isChunkKey(v["key"]) &&
    isLocalVoxelCoord(v["x"]) &&
    isLocalVoxelCoord(v["y"]) &&
    isLocalVoxelCoord(v["z"]) &&
    isBlockId(v["block"]) &&
    isInteger(v["rev"])
  );
}

/**
 * Structural guard for client frames. Validates every field of every variant and rejects any
 * unknown `t`, so a Worker can trust the result of `isClientMessage(JSON.parse(raw))`.
 */
export function isClientMessage(v: unknown): v is ClientMessage {
  if (!isRecord(v)) {
    return false;
  }
  switch (v["t"]) {
    case "hello":
      return isInteger(v["v"]) && isString(v["name"]);
    case "input":
      return (
        isInteger(v["seq"]) &&
        isFiniteNumber(v["dt"]) &&
        isVec3(v["move"]) &&
        isVec3(v["look"]) &&
        isInteger(v["actions"])
      );
    case "setBlock":
      return isBlockEdit(v);
    case "sub": {
      const keys = v["keys"];
      return Array.isArray(keys) && (keys as unknown[]).every(isChunkKey);
    }
    case "ping":
      return isFiniteNumber(v["ts"]);
    case "unsub": {
      const keys = v["keys"];
      return Array.isArray(keys) && (keys as unknown[]).every(isChunkKey);
    }
    default:
      return false;
  }
}

function isServerMessage(v: unknown): v is ServerMessage {
  if (!isRecord(v)) {
    return false;
  }
  switch (v["t"]) {
    case "welcome":
      return (
        isString(v["playerId"]) &&
        isInteger(v["tick"]) &&
        isInteger(v["worldSeed"]) &&
        isVec3(v["spawn"]) &&
        isWorldConfig(v["config"]) &&
        // Optional and additive: a NULLISH value is exactly as absent as a missing field (a peer may
        // serialize an absent optional as an explicit `null`), and rejecting it would make this
        // optional field able to drop the whole `welcome` - i.e. the client would never get a world.
        // A present, non-nullish value is still strict: it must be an array of strings.
        (v["capabilities"] == null ||
          (Array.isArray(v["capabilities"]) && (v["capabilities"] as unknown[]).every(isString)))
      );
    case "snapshot": {
      const players = v["players"];
      return isInteger(v["tick"]) && Array.isArray(players) && (players as unknown[]).every(isPlayerState);
    }
    case "chunk":
      return isChunkSnapshot(v["snapshot"]);
    case "ack":
      return isInteger(v["seq"]) && isInteger(v["tick"]) && isVec3(v["pos"]) && isVec3(v["vel"]);
    case "blockAck":
      return isBlockEdit(v) && isBoolean(v["ok"]);
    case "pong":
      return isFiniteNumber(v["ts"]);
    case "error":
      return isString(v["code"]) && isString(v["message"]);
    default:
      return false;
  }
}
